import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * Dock Tab Group Component
 * 
 * Manages a group of tabs within a dock region.
 * Handles tab switching and renders the active tab's content.
 */
@customElement('dock-tab-group')
export class DockTabGroup extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }
    
    :host(.collapsed) .tab-content {
      display: none;
    }

    .tab-header {
      display: flex;
      gap: 2px;
      padding: 4px;
      background: rgba(0, 0, 0, 0.3);
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      overflow-x: auto;
      overflow-y: hidden;
      flex-shrink: 0;
      align-items: center;
    }
    
    .tabs-row {
      display: flex;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }
    
    .collapse-btn {
      padding: 4px 8px;
      border: none;
      background: rgba(255, 255, 255, 0.05);
      color: #aaa;
      cursor: pointer;
      font-size: 11px;
      border-radius: 3px;
      transition: all 0.2s;
      margin-left: 4px;
      flex-shrink: 0;
    }
    
    .collapse-btn:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }

    .tab-header::-webkit-scrollbar {
      height: 4px;
    }

    .tab-header::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.2);
    }

    .tab-header::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    .tab-button {
      padding: 6px 12px;
      border: none;
      background: rgba(255, 255, 255, 0.05);
      color: #aaa;
      cursor: pointer;
      font-size: 11px;
      font-weight: 500;
      border-radius: 4px;
      white-space: nowrap;
      transition: all 0.2s;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .tab-button:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #eee;
    }

    .tab-button.active {
      background: rgba(33, 150, 243, 0.3);
      color: #fff;
      box-shadow: 0 0 8px rgba(33, 150, 243, 0.4);
    }

    .tab-close {
      padding: 0;
      margin: 0;
      width: 14px;
      height: 14px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      color: #aaa;
      border-radius: 2px;
      cursor: pointer;
      font-size: 10px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .tab-close:hover {
      background: rgba(255, 80, 80, 0.6);
      color: #fff;
    }

    .tab-content {
      flex: 1;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    ::slotted(*) {
      display: none !important;
      height: 100%;
    }

    ::slotted([active]) {
      display: flex !important;
      flex-direction: column;
    }
  `;

  @property({ type: String }) activeTab = '';
  @property({ type: Boolean }) collapsed = false;
  @property({ type: String }) region: 'left' | 'right' | 'bottom' = 'left';
  @state() private tabs: Array<{ id: string; title: string }> = [];

  private observer?: MutationObserver;

  connectedCallback(): void {
    super.connectedCallback();
    this.updateTabs();
    this.observeSlotChanges();
  }

  disconnectedCallback(): void {
    this.observer?.disconnect();
    super.disconnectedCallback();
  }

  private observeSlotChanges(): void {
    this.observer = new MutationObserver(() => {
      this.updateTabs();
    });

    this.observer.observe(this, {
      childList: true,
      subtree: false,
    });
  }

  private updateTabs(): void {
    const slotElements = Array.from(this.children) as HTMLElement[];
    this.tabs = slotElements
      .filter((el) => el.tagName === 'DOCK-TAB')
      .map((el) => ({
        id: el.getAttribute('tab-id') || '',
        title: el.getAttribute('title') || 'Untitled',
      }));

    // Set first tab as active if none selected
    if (!this.activeTab && this.tabs.length > 0) {
      this.activeTab = this.tabs[0].id;
    }

    this.updateActiveTab();
  }

  private updateActiveTab(): void {
    const slotElements = Array.from(this.children) as HTMLElement[];
    slotElements.forEach((el) => {
      if (el.tagName === 'DOCK-TAB') {
        const tabId = el.getAttribute('tab-id');
        if (tabId === this.activeTab) {
          el.setAttribute('active', '');
        } else {
          el.removeAttribute('active');
        }
      }
    });
  }

  private handleTabClick(tabId: string): void {
    if (this.activeTab === tabId) return;

    this.activeTab = tabId;
    this.updateActiveTab();

    this.dispatchEvent(new CustomEvent('tab-changed', {
      detail: { tabId },
      bubbles: true,
      composed: true,
    }));
  }

  private handleTabDragStart(e: DragEvent, tabId: string): void {
    if (!e.dataTransfer) return;
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/panel-id', tabId);
    
    // Add visual feedback
    (e.target as HTMLElement).style.opacity = '0.5';
    
    this.dispatchEvent(new CustomEvent('tab-drag-start', {
      detail: { panelId: tabId },
      bubbles: true,
      composed: true,
    }));
  }

  private handleTabDragEnd(e: DragEvent): void {
    (e.target as HTMLElement).style.opacity = '1';
  }

  private handleTabClose(e: Event, tabId: string): void {
    e.stopPropagation(); // Prevent tab activation
    
    this.dispatchEvent(new CustomEvent('tab-close', {
      detail: { tabId },
      bubbles: true,
      composed: true,
    }));
  }
  
  private handleCollapse(): void {
    this.collapsed = !this.collapsed;
    if (this.collapsed) {
      this.classList.add('collapsed');
    } else {
      this.classList.remove('collapsed');
    }
    
    // Dispatch event to parent dock-region to handle actual collapse
    this.dispatchEvent(new CustomEvent('region-collapse', {
      detail: { collapsed: this.collapsed },
      bubbles: true,
      composed: true,
    }));
  }

  private getCollapseIcon() {
    const isBottom = this.region === 'bottom';
    const style = isBottom ? 'transform: rotate(90deg); display: inline-block;' : '';
    
    if (this.collapsed) {
      // Expand icons
      switch (this.region) {
        case 'left': return html`»`;
        case 'right': return html`«`;
        case 'bottom': return html`<span style="${style}">«</span>`; // Points up
      }
    } else {
      // Collapse icons
      switch (this.region) {
        case 'left': return html`«`;
        case 'right': return html`»`;
        case 'bottom': return html`<span style="${style}">»</span>`; // Points down
      }
    }
    return html`▼`;
  }

  render() {
    return html`
      <div class="tab-header">
        <div class="tabs-row">
          ${this.tabs.map((tab) => html`
            <button
              class="tab-button ${tab.id === this.activeTab ? 'active' : ''}"
              draggable="true"
              @click=${() => this.handleTabClick(tab.id)}
              @dragstart=${(e: DragEvent) => this.handleTabDragStart(e, tab.id)}
              @dragend=${this.handleTabDragEnd}
            >
              <span>${tab.title}</span>
              <button 
                class="tab-close"
                @click=${(e: Event) => this.handleTabClose(e, tab.id)}
                title="Close ${tab.title}"
              >×</button>
            </button>
          `)}
        </div>
        <button 
          class="collapse-btn"
          @click=${this.handleCollapse}
          title="${this.collapsed ? 'Expand' : 'Collapse'} panel"
        >${this.getCollapseIcon()}</button>
      </div>
      <div class="tab-content">
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dock-tab-group': DockTabGroup;
  }
}
